import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { api } from "../../api/client";
import type { AssetGroup, RoadDetail } from "../../api/types";
import { Confidence, StatusChip } from "../../components/Badges";
import { DataTable } from "../../components/DataTable";
import { Panel } from "../../components/Layout";
import { EmptyState, QueryState, SkeletonBlock } from "../../components/States";
import { chainage } from "../../lib/format";
import { FindingDetailPanel } from "./FindingsTab";

/** Consolidated asset inventory: repeat detections of one physical asset merged into one row. */
export function AssetsTab({ road }: { road: RoadDetail }) {
  const sid = road.survey.survey_uuid;
  const [params, setParams] = useSearchParams();
  const sel = params.get("a");
  const q = useQuery({ queryKey: ["road", sid, "assets"], queryFn: () => api<{ items: AssetGroup[] }>(`/api/roads/${sid}/assets`) });
  const selected = q.data?.items.find((g) => g.group_uuid === sel);

  return (
    <div className="split">
      <Panel title="Asset inventory" id="as-list" flush>
        <QueryState query={q} skeleton={<div className="panel-body"><SkeletonBlock rows={6} /></div>}>
          {(d) => (
            <DataTable<AssetGroup>
              rows={d.items}
              rowKey={(r) => r.group_uuid}
              selectedKey={sel}
              onRowClick={(r) => setParams({ a: r.group_uuid }, { replace: true })}
              initialSort={{ key: "ch", dir: "asc" }}
              caption="Asset inventory"
              empty={<EmptyState title="No assets recorded on this road">Assets appear here after AI analysis groups repeat detections of the same sign, barrier or culvert.</EmptyState>}
              columns={[
                { key: "ch", label: "Chainage", sort: (r) => r.inventory_chainage_m ?? r.start_chainage_m, render: (r) => (
                  <span className="mono">{r.is_linear && r.end_chainage_m ? `${chainage(r.start_chainage_m)} – ${chainage(r.end_chainage_m)}` : chainage(r.inventory_chainage_m ?? r.start_chainage_m)}</span>) },
                { key: "asset", label: "Asset", sort: (r) => r.asset_name, render: (r) => <>{r.sign_name || r.asset_name}<small className="muted" style={{ display: "block" }}>{r.category}</small></> },
                { key: "side", label: "Side", sort: (r) => r.side_of_road, render: (r) => r.side_of_road || "—" },
                { key: "n", label: "Seen in", align: "right", sort: (r) => r.member_count, render: (r) => `${r.member_count} frame${r.member_count === 1 ? "" : "s"}` },
                { key: "conf", label: "Confidence", sort: (r) => r.confidence, render: (r) => <Confidence value={r.confidence} /> },
                { key: "st", label: "Status", sort: (r) => r.status, render: (r) => <StatusChip status={r.status} /> },
              ]}
            />
          )}
        </QueryState>
      </Panel>
      <Panel title="Evidence" id="as-evidence">
        {selected?.detection_uuid ? <FindingDetailPanel id={`asset:${selected.detection_uuid}`} compact /> : (
          <EmptyState title="Select an asset">Choose a row to see the clearest image of it.</EmptyState>
        )}
      </Panel>
    </div>
  );
}
